import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { Radio, At, Lock, Eye, EyeSlash } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const loginTexts = {
  de: {
    welcomeBack: "Willkommen zurück",
    subtitle: "Melde dich an und setze deine Reise fort",
    emailOrUsername: "E-Mail oder Benutzername",
    emailPlaceholder: "you@example.com oder username",
    password: "Passwort",
    login: "Einloggen",
    loggingIn: "Einloggen...",
    forgotPassword: "Passwort vergessen?",
    noAccount: "Noch kein Konto?",
    signUp: "Registrieren",
    backToHome: "Zurück zur Startseite",
    missingFields: "Bitte E-Mail oder Username und Passwort eingeben.",
    loginFailed: "Login fehlgeschlagen.",
    hidePassword: "Passwort ausblenden",
    showPassword: "Passwort anzeigen",
  },

  en: {
    welcomeBack: "Welcome back",
    subtitle: "Log in to continue your journey",
    emailOrUsername: "Email or Username",
    emailPlaceholder: "you@example.com or username",
    password: "Password",
    login: "Log in",
    loggingIn: "Logging in...",
    forgotPassword: "Forgot password?",
    noAccount: "Don’t have an account?",
    signUp: "Sign up",
    backToHome: "Back to home",
    missingFields: "Please enter email or username and password.",
    loginFailed: "Login failed.",
    hidePassword: "Hide password",
    showPassword: "Show password",
  },

  no: {
    welcomeBack: "Velkommen tilbake",
    subtitle: "Logg inn for å fortsette reisen din",
    emailOrUsername: "E-post eller brukernavn",
    emailPlaceholder: "you@example.com eller brukernavn",
    password: "Passord",
    login: "Logg inn",
    loggingIn: "Logger inn...",
    forgotPassword: "Glemt passord?",
    noAccount: "Har du ikke konto?",
    signUp: "Registrer deg",
    backToHome: "Tilbake til startsiden",
    missingFields: "Skriv inn e-post eller brukernavn og passord.",
    loginFailed: "Innlogging mislyktes.",
    hidePassword: "Skjul passord",
    showPassword: "Vis passord",
  },

  ko: {
    welcomeBack: "다시 오신 것을 환영합니다",
    subtitle: "계속하려면 로그인하세요",
    emailOrUsername: "이메일 또는 사용자 이름",
    emailPlaceholder: "you@example.com 또는 사용자 이름",
    password: "비밀번호",
    login: "로그인",
    loggingIn: "로그인 중...",
    forgotPassword: "비밀번호를 잊으셨나요?",
    noAccount: "계정이 없으신가요?",
    signUp: "가입하기",
    backToHome: "홈으로 돌아가기",
    missingFields: "이메일 또는 사용자 이름과 비밀번호를 입력하세요.",
    loginFailed: "로그인에 실패했습니다.",
    hidePassword: "비밀번호 숨기기",
    showPassword: "비밀번호 보기",
  },

  fi: {
    welcomeBack: "Tervetuloa takaisin",
    subtitle: "Kirjaudu sisään jatkaaksesi",
    emailOrUsername: "Sähköposti tai käyttäjänimi",
    emailPlaceholder: "you@example.com tai käyttäjänimi",
    password: "Salasana",
    login: "Kirjaudu sisään",
    loggingIn: "Kirjaudutaan...",
    forgotPassword: "Unohditko salasanan?",
    noAccount: "Eikö sinulla ole tiliä?",
    signUp: "Rekisteröidy",
    backToHome: "Takaisin etusivulle",
    missingFields: "Syötä sähköposti tai käyttäjänimi ja salasana.",
    loginFailed: "Kirjautuminen epäonnistui.",
    hidePassword: "Piilota salasana",
    showPassword: "Näytä salasana",
  },
};

const Login = () => {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);

  const navigate = useNavigate();
  const { login, user, loading } = useAuth();
  const { language } = useLanguage();

  const text = loginTexts[language] || loginTexts.en;

  useEffect(() => {
    if (!loading && user) {
      navigate("/discover", { replace: true });
    }
  }, [user, loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const cleanIdentifier = identifier.trim().toLowerCase();

    if (!cleanIdentifier || !password) {
      setError(text.missingFields);
      return;
    }

    setError("");
    setSubmitLoading(true);

    const result = await login(cleanIdentifier, password);

    setSubmitLoading(false);

    if (result.success) {
      navigate("/discover", { replace: true });
      return;
    }

    setError(result.error || text.loginFailed);
  };

  const isButtonDisabled = submitLoading || loading;

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-8">
          <Radio size={40} weight="duotone" className="text-cyan-400" />
          <span
            className="text-3xl font-black tracking-tighter text-white"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            WORLDofRADIO
          </span>
        </div>

        <div className="backdrop-filter backdrop-blur-2xl bg-black/60 border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)] rounded-3xl p-8">
          <h1
            className="text-2xl sm:text-3xl tracking-tight font-bold mb-2 text-white"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {text.welcomeBack}
          </h1>

          <p className="text-sm text-gray-400 mb-8">{text.subtitle}</p>

          {error && (
            <div
              data-testid="login-error"
              className="bg-red-500/10 border border-red-500/50 rounded-xl p-4 mb-6 text-sm text-red-400"
              role="alert"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="identifier" className="text-white mb-2 block">
                {text.emailOrUsername}
              </Label>

              <div className="relative">
                <At
                  size={20}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                />

                <Input
                  data-testid="login-email-input"
                  id="identifier"
                  name="identifier"
                  type="text"
                  autoComplete="username"
                  value={identifier}
                  onChange={(e) => {
                    setIdentifier(e.target.value);
                    if (error) setError("");
                  }}
                  className="pl-12 bg-[#0A0E17] border-white/10 text-white h-12 rounded-xl focus:border-cyan-500 focus:ring-cyan-500/20"
                  placeholder={text.emailPlaceholder}
                  required
                  disabled={isButtonDisabled}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-white mb-2 block">
                {text.password}
              </Label>

              <div className="relative">
                <Lock
                  size={20}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                />

                <Input
                  data-testid="login-password-input"
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (error) setError("");
                  }}
                  className="pl-12 pr-12 bg-[#0A0E17] border-white/10 text-white h-12 rounded-xl focus:border-cyan-500 focus:ring-cyan-500/20"
                  placeholder="••••••••"
                  required
                  disabled={isButtonDisabled}
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-cyan-400 transition-colors disabled:opacity-50"
                  aria-label={showPassword ? text.hidePassword : text.showPassword}
                  disabled={isButtonDisabled}
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={isButtonDisabled}
              className="w-full h-12 bg-cyan-400 hover:bg-cyan-300 text-black font-bold rounded-xl transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitLoading ? text.loggingIn : text.login}
            </Button>
          </form>

          <div className="text-center mt-6 space-y-4">
            <Link
              to="/forgot-password"
              className="inline-block text-sm text-cyan-400 hover:text-cyan-300 font-semibold transition-colors"
            >
              {text.forgotPassword}
            </Link>

            <p className="text-sm text-gray-400">
              {text.noAccount}{" "}
              <Link
                to="/register"
                className="text-cyan-400 hover:text-cyan-300 font-semibold"
              >
                {text.signUp}
              </Link>
            </p>
          </div>
        </div>

        <div className="text-center mt-6">
          <Link
            to="/"
            className="text-sm text-gray-400 hover:text-cyan-400 transition-colors"
          >
            ← {text.backToHome}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;